<main id="main-content" class="main-content">
  @if (errorList.length > 0) {
    <div class="red-warning">
      <mat-icon class="red-warn-icon">warning</mat-icon>
      <div class="red-warn-text">
        <span class="red-title">Incomplete section</span>
        <span class="red-text">
          The following fields are missing details. Please complete them before proceeding to the next section:
          <ul>
            @for (error of errorList; track error) {
              <li>{{error | titlecase}}</li>
            }
          </ul>
        </span>
      </div>
    </div>
  }
  <div class="subtitle"><span class="red-normal">*</span>Indicates required field</div>
  <h2 class="form-header">{{page()}}. Recommended Treatment Narratives</h2>
  <div class="subtitle-big" style="line-height: 30px;">
    Add details to each treatment card. If no treatment cards have been loaded from GIS, manually add treatments by
    selecting Add New Treatment.
    <br>
      Note: The total cost for implementation of all treatment recommendations is for the full calendar year.
    </div>

    @if (fromGIS) {
      <div class="blue-warning">
        <fa-icon [icon]="faCircleInfo" class="blue-warn-icon" aria-label="Warning"></fa-icon>
        <div class="support-text">
          <b style="margin-left: 10px;">The treatments below have been sourced from GIS data.</b>
          <ul>
            <li>
              For manual treatment entries when GIS data is added, GIS acres will be appended so you can choose
              between 'Unit Qty (GIS)' or 'Custom Unit Qty'.
            </li>
            <li>
              If your unit quantities do not match, please coordinate with the GIS Specialist to update the 'Unit
              Qty (GIS)' field, or deselect the 'Override unit qty from GIS' option to apply the 'Unit Qty (GIS)'
              value and ensure the Total Cost is accurate.
            </li>
          </ul>
        </div>
      </div>
    }
    @for (forest of forests; track forest; let i = $index) {
      <div class="forest-container">
        <h2 class="forest-title">{{ forest.forest_name }}</h2>
        <h3 class="treatment-title">Treatments <span class="thin"><i>(Source: GIS):</i></span> {{ countGISTreatments(i)
        }} <span class="nav-status-0">|</span> New Treatments: {{ countNewTreatments(i) }} <span
      class="nav-status-0">|</span> Total Treatments: {{ treatments[i].length }}</h3>
      <button class="usa-button usa-button--secondary medium-length add-treatment-btn" (click)="addNewTreatment(i)">
        <mat-icon>add</mat-icon>Add New Treatment
      </button>
      <span class="gray-text" style="margin-bottom: 10px;"><b>Tip:</b> To expand or collapse all treatment cards,
    select “Ctrl” on your keyboard and click over a treatment card.</span>
    <mat-accordion [multi]="true" #accordion="matAccordion">
      @for (treatment of treatments[i]; track treatment; let j = $index) {
        <mat-expansion-panel class="treatment-container" expanded="false" hideToggle
          (opened)="treatment.open = true"
          (closed)="treatment.open = false" (click)="onTreatmentClick($event, accordion)"
          (onkeypress)="onTreatmentKeypress($event, accordion)" onkeypress="">
          <mat-expansion-panel-header>
            <mat-panel-title class="treatment-title">
              @if (treatment.open) {
                <i class="material-icons green-arrow" role="img">keyboard_arrow_down</i>
              }
              @if (!treatment.open) {
                <i class="material-icons green-arrow" role="img">chevron_right</i>
              }
              @if(
                treatment.new
                && ((treatment.specs[0].form.value.unit_cost == null)
                && (treatment.form?.value?.treatment_type?.unit_of_measure == null)
                && (treatment.form?.value?.treatment_option == null)
                )){
                <mat-icon class="icon-status nav-status-0" matTooltip='Not Started'
                matTooltipClass="tooltipClass">panorama_fish_eye</mat-icon>
              }
              @else if(
              (treatment.new && ((treatment.specs[0].form.value.custom_unit_qty ?? '') == ''))
              || (!treatment.new && treatment.specs[0].form.value.override_GIS_qty &&
              ((treatment.specs[0].form.value.custom_unit_qty ?? '') == ''))
              || ((treatment.specs[0].form.value.unit_cost ?? '') == '')
              || (treatment.form?.value?.treatment_type?.unit_of_measure == null)
              || (treatment.form?.value?.treatment_option == null)
              || (treatment.specs[0].form.value.specification_number != 1 &&
              treatment.specs[0].form.value.specification_name == '')
              ){
              <mat-icon class="icon-status nav-status-1" matTooltip='In Progress'
              matTooltipClass="tooltipClass">contrast</mat-icon>
            }
            @else {
            <mat-icon class="icon-status nav-status-2" matTooltip='Complete'
            matTooltipClass="tooltipClass">check_circle</mat-icon>
          }
          <div class="treatment-title-info">
            <div>{{ j+1 }}. {{ forest.forest_name }}</div>
            <span class="thin">
              {{ treatmentOptionNameMap.get(treatment.form.value.treatment_option) }}
              ({{treatment.form.value.treatment_type?.unit_of_measure}})
              (Spec.{{treatment.specs[0].form.value.specification_number}})
            </span>
          </div>
          @if (treatment.new) {
            <mat-chip class="mat-chip-new">
              NEW
            </mat-chip>
          }
          @if (treatment.new && treatment.specs[0]?.form.value.from_GIS === true && treatment.specs[0]?.form.value.override_GIS_qty === true && treatment.specs[0]?.form.value.gis_unit_qty != treatment.specs[0]?.form.value.custom_unit_qty) {
            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"
              style="margin-right: -16px; width: 0;"
            ></fa-icon>
          }
        </mat-panel-title>
      </mat-expansion-panel-header>
      @if (treatment.form) {
        <div [formGroup]="treatment.form">
          @if (treatment.new && treatment.specs[0]?.form.value.from_GIS === false) {
            <div>
              <ng-container *ngTemplateOutlet="gisWarn"></ng-container>
            </div>
          }
          @if (treatment.new && treatment.specs[0]?.form.value.from_GIS === true) {
            <div>
              <ng-container *ngTemplateOutlet="gisWarn2"></ng-container>
            </div>
          }
          @if (treatment.matchRecord) {
            <div class="whole-line">
              <button class="usa-button usa-button--secondary copy-treatment-btn"
                (click)="copyTreatment(treatment)">
                <fa-icon [icon]="faCopy" class="fa-regular fa-green" aria-label="close"></fa-icon>
                Copy Details from Previous Forest
              </button>
              <mat-icon class="blue-warn-icon" aria-label="Warning" matTooltipClass="tooltipClass"
                matTooltip='If the same treatment and spec. exists on both forests, this button will  duplicate the “unit cost”, “treatment narrative”, and  “spec. sheet file link” from a previous treatment entry.'
              style="margin-top: 18px; font-size: 28px;width: 28px;height: 28px;">info_outline</mat-icon>
            </div>
          }
          <div class="whole-line">
            <mat-form-field appearance="outline" class="half-field">
              <mat-label>Type</mat-label>
              <mat-select formControlName="treatment_type" required
                (selectionChange)="treatmentTypeSelected($event, treatment)">
                @for (treat of treatmentTypes; track treat) {
                  <mat-option [value]="treat">{{treat.type}}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="half-field">
              <mat-label>Treatment</mat-label>
              <mat-select formControlName="treatment_option" required [disabled]="treatment.type == null"
                (selectionChange)="standardTreatmentSelected($event, treatment, i)">
                @for (treat of treatment.type?.standardTreatments; track treat) {
                  <mat-option [value]="treat.treatment_option_id">{{treat.treatment_option}}
                  ({{treat.unit_of_measure}})</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <div class="whole-line treatment-link">
            <div class="half-field"></div>
            <div class="half-field">
              <a target="_blank" rel="noopener noreferrer"
                href="https://usdagcc.sharepoint.com/:x:/r/sites/fs-baer/_layouts/15/Doc.aspx?sourcedoc=%7BA737B98B-F3EE-44ED-BCCD-9B49CC65BCED%7D&file=BAERStandardTreatment_Revised2025.xlsx">
                Treatment Reference Table <fa-icon [icon]="faArrowUpRightFromSquare"
              class="fa-regular fa-green" aria-label="close"></fa-icon></a>
            </div>
          </div>
          @for (spec of treatment.specs; track spec; let k = $index) {
            <div class="specification"
              [formGroup]="spec.form">
              @if (k>0) {
                <button class="delete-button" (click)="removeSpec(i,j,k,spec.treatment_specification_id)"
                  matTooltip='Remove Spec.' matTooltipClass="tooltipClass">
                  <fa-icon [icon]="faCircleXmark" class="fa-regular fa-circle-mark delete-row-icon"
                  aria-label="close"></fa-icon>
                </button>
              }
              @if ((treatment.new && spec.form.value.from_GIS == false) || (!treatment.new && spec.form.value.specification_number != 1) || (treatment.new && spec.form.value.from_GIS == true && spec.form.value.specification_number != 1)) {
                <div class="whole-line" style="margin-bottom: 16px;"
                  >
                  <div class="spec-container">
                    @if (spec.form.value.from_GIS == false) {
                      <span class="red-normal">*</span>
                      }<b>Spec.
                    #</b>
                    <br>
                      @if (spec.form.value.from_GIS == false) {
                        <mat-form-field appearance="outline" style="max-width: 120px;" class="name-field"
                          >
                          <mat-select formControlName="specification_number">
                            @for (a of [1,2,3,4,5,6]; track a) {
                              <mat-option [value]="a">{{a}}</mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                      }
                      @if ((spec.form.value.from_GIS ?? true) == true) {
                        <span
                        style="font-size: 20px;position: relative;top: 20px;left: 20px;">{{spec.form.value.specification_number}}</span>
                      }
                    </div>
                    @if (spec.form.value.specification_number != 1) {
                      <div class="name-container">
                        <span class="red-normal">*</span><b>Implementation Spec. Name</b>
                        <br>
                          <mat-form-field required appearance="outline" class="name-field">
                            <input matInput formControlName="specification_name"
                              placeholder="Enter Specification Name" required>
                            </mat-form-field>
                            <br>
                              @if (spec.form.value.specification_name == '') {
                                <span class="gray-text">100
                                  characters
                                allowed</span>
                              }
                              @if (spec.form.value.specification_name != '') {
                                <span class="gray-text">{{
                                100-spec.form.value.specification_name?.length }} characters remaining</span>
                              }
                            </div>
                          }
                        </div>
                      }
                      @if (treatment.new && treatment.specs[0]?.form.value.from_GIS === true && treatment.specs[0]?.form.value.override_GIS_qty === true && treatment.specs[0]?.form.value.gis_unit_qty != treatment.specs[0]?.form.value.custom_unit_qty) {
                        <div class="yellow-warning"
                          >
                          <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon>
                          <div class="support-text">
                            <b>“Unit Qty (GIS)” and “Custom Unit Qty” don't match.</b>
                            <p style="max-width: 1200px;">If your unit quantities do not match, please coordinate
                              with the GIS Specialist to
                              update the 'Unit Qty (GIS)' field,
                              or deselect the 'Override unit qty from GIS' option to apply the 'Unit Qty (GIS)'
                              value and ensure the Total Cost is accurate.
                            </p>
                          </div>
                        </div>
                      }
                      <div class="whole-line" style="flex-wrap: wrap;">
                        <div class="quarter-div field-div">
                          <mat-form-field appearance="outline" class="quarter-field" style="margin-left: 0;">
                            <mat-label>Unit Cost</mat-label>
                            <input required [disabled]="treatment.type == null" prefix="$ " mask="separator.2"
                              thousandSeparator="," decimalMarker="." matInput aria-label="Unit Cost"
                              formControlName="unit_cost">
                            </mat-form-field>
                          </div>
                          <div class="quarter-div">
                            <p class="data-title">Unit of Measure</p>
                            <p class="data-info">{{ treatment.type?.unit_of_measure ?? "--"}}</p>
                          </div>
                          <div class="quarter-div">
                            <p class="data-title">Unit Qty (GIS)</p>
                            <p class="data-info">{{ spec.gis_unit_qty }}</p>
                            @if (spec.form.value.from_GIS === false) {
                              <p class="data-info"><i>Not Available</i>
                            </p>
                          }
                        </div>
                        @if (spec.form.value.override_GIS_qty || spec.form.value.from_GIS === false) {
                          <div class="quarter-div field-div"
                            >
                            <mat-form-field appearance="outline" class="quarter-field">
                              <mat-label>Custom Unit Qty</mat-label>
                              <input required matInput aria-label="Custom Unit Quanity"
                                formControlName="custom_unit_qty" thousandSeparator="," mask="separator"
                                decimalMarker=".">
                              </mat-form-field>
                            </div>
                          }
                        </div>
                        <div class="whole-line" style="flex-wrap: wrap">
                          <div class="quarter-div">
                            <p class="data-title">Total Cost</p>
                            @if (!spec.form.value.override_GIS_qty && !(spec.form.value.from_GIS === false)) {
                              <p
                                class="data-info">${{ (spec.gis_unit_qty *
                              spec.form.value.unit_cost) | number: '1.2-2' }}</p>
                            }
                            @if (spec.form.value.override_GIS_qty || spec.form.value.from_GIS === false) {
                              <p
                                class="data-info">${{
                                (spec.form.value.custom_unit_qty * spec.form.value.unit_cost) | number: '1.2-2'
                                }}
                              </p>
                            }
                          </div>
                        </div>
                        @if(spec.form.value.from_GIS !== false) {
                          <div class="whole-line">
                            <mat-checkbox formControlName="override_GIS_qty">
                              Override Unit Qty from GIS.
                              <br>
                                <span class="gray-text">Please document the change in the treatment
                                narrative.</span>
                              </mat-checkbox>
                            </div>
                            <div class="text-div">
                              <b>Includes treatments off-NFS land? </b> <span class="gray-text"> (GIS Data)</span>
                              <br>
                                <span class="gray-text">If yes, provide details in the Treatment Narrative.</span>
                              </div>
                              <div class="text-div">
                                @if ((spec.form.value.from_GIS !== false) && (spec.form.value.treatment_off_nfs_land == null)) {
                                Does Not Apply
                                } @else {
                                {{ spec.form.value.treatment_off_nfs_land? "Yes" : "No" }}
                                }
                              </div>
                              <div class="text-div">
                                <b>Includes treatments outside the Fire Boundary? </b> <span class="gray-text"> (GIS
                              Data)</span>
                              <br>
                                <span class="gray-text">If yes, provide details in the Treatment Narrative.</span>
                              </div>
                              <div class="text-div">
                                @if ((spec.form.value.from_GIS !== false) && (spec.form.value.treatment_outside_fire == null)) {
                                Does Not Apply
                                } @else {
                                {{ spec.form.value.treatment_outside_fire? "Yes" : "No" }}
                                }
                              </div>
                            }
                            <div class="text-div">
                              <b>Treatment Narrative</b>
                              <br>
                                <span class="gray-text">Add a description and include a justification when treatments
                                  are
                                  needed off-NFS land or outside the fire boundary. The total cost for implementation
                                  is
                                for the full calendar year.</span>
                              </div>
                              <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
                              <div class="NgxEditor__Wrapper">
                                <ngx-editor-menu [editor]="spec.editor" [toolbar]="toolbar"></ngx-editor-menu>
                                <ngx-editor formControlName="treatment_narrative" [editor]="spec.editor"
                                placeholder="Enter Description"></ngx-editor>
                              </div>
                              <span class="gray-text text-count"
                                [ngClass]="{ 'text-count-red': spec.form.controls.treatment_narrative.invalid }">{{spec.form.controls.treatment_narrative.value?.length
                              ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
                              @if (spec.form.controls.treatment_narrative.invalid) {
                                <div class="editor-error">
                                  <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please
                                  shorten your text or remove hidden formatting (e.g., paste as plain text using
                                  Ctrl+Shift+V).
                                </div>
                              }
                              <div class="text-div">
                                <b>Specification Sheet file link (optional)</b>
                              </div>
                              <div class="whole-line">
                                <mat-form-field appearance="outline" class="whole-field">
                                  <input matInput formControlName="specification_sheet"
                                    aria-label="Specification Sheet file link (optional)"
                                    placeholder="Enter FS Box url e.g. www.box.com/">
                                    <mat-error>Please enter a valid URL.</mat-error>
                                  </mat-form-field>
                                </div>
                              </div>
                            }
                            <div class="whole-line">
                              <button class="usa-button usa-button--secondary medium-length delete-treatment-btn"
                                (click)="deleteTreatment(i, j, treatment.forest_treatment_id)">
                                <fa-icon class="fa-sm trash-icon" [icon]="faTrash"></fa-icon> Remove Treatment
                              </button>
                            </div>
                          </div>
                        }
                      </mat-expansion-panel>
                    }
                  </mat-accordion>
                </div>
              }
            </main>

            <ng-template #gisWarn>
              <div class="blue-warning">
                <fa-icon [icon]="faCircleInfo" class="blue-warn-icon" aria-label="Warning"></fa-icon>
                <div class="support-text">
                  <b>GIS data has not been loaded into this treatment.</b>
                  <ul>
                    <li>
                      Manually entered treatments don’t exist in GIS database until added by GIS specialist.
                    </li>
                    <li>
                      If the treatment is added in GIS, and loaded into this report, the data will be displayed under
                      “Unit Qty (GIS)”.
                    </li>
                    <li>
                      If you want to keep the entered “Custom Unit Qty”, keep the “Override Unit Qty from GIS” option
                      selected.
                    </li>
                  </ul>
                </div>
              </div>
            </ng-template>

            <ng-template #gisWarn2>
              <div class="blue-warning">
                <fa-icon [icon]="faCircleInfo" class="blue-warn-icon" aria-label="Warning"></fa-icon>
                <div class="support-text" style="padding: 0;">
                  <b>GIS data has been loaded into this treatment.</b>
                </div>
              </div>
            </ng-template>